/**
 * Error model for the DeCA API.
 *
 * Every non-2xx response in the contract is `application/problem+json` (RFC 9457).
 * The one that matters most for the portal is the pre-generation validation failure:
 * it carries `partyErrors`, grouping missing fields by the party liable for them, plus
 * the €401-per-party penalty notice. Design spec §3 ("Per-party validation") turns that
 * straight into the blocked-generation screen, so it is worth first-class typing rather
 * than a cast at the call site.
 */

import type { components } from './schema'

export type Problem = components['schemas']['Problem']
export type ValidationProblem = components['schemas']['ValidationProblem']
export type PartyErrors = components['schemas']['PartyErrors']
export type FieldError = components['schemas']['FieldError']
export type LiableParty = components['schemas']['LiableParty']

/**
 * Thrown by `unwrap()`. Carries the parsed problem document, so UI code switches on
 * `code` — never on `title`, which is localised by `Accept-Language` and will change
 * under the reader's feet.
 */
export class DecaApiError extends Error {
  readonly problem: Problem
  readonly status: number
  readonly code: string
  readonly traceId?: string
  readonly response?: Response

  constructor(problem: Problem, response?: Response) {
    super(problem.detail || problem.title || `HTTP ${problem.status}`)
    this.name = 'DecaApiError'
    this.problem = problem
    this.status = problem.status
    this.code = problem.code
    this.traceId = problem.traceId
    this.response = response
  }

  /** True when the server rejected the document itself, not the request's shape. */
  get isValidation(): boolean {
    return isValidationProblem(this.problem)
  }

  /** `412`/`409` from an `If-Match` that no longer matches — someone else edited it. */
  get isVersionConflict(): boolean {
    return this.status === 412 || this.code === 'VERSION_CONFLICT'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }

  /** Seconds to wait, from the `Retry-After` header on a 429. */
  get retryAfterSeconds(): number | undefined {
    const raw = this.response?.headers.get('retry-after')
    if (!raw) return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  }
}

/** Does this problem carry the per-party validation payload? */
export function isValidationProblem(p: Problem | undefined | null): p is ValidationProblem {
  return !!p && Array.isArray((p as ValidationProblem).partyErrors)
}

/**
 * Anything shaped like an RFC 9457 document. Deliberately loose: a proxy, a 502 from
 * Caddy, or a bug can produce a body we did not author, and the portal should degrade
 * to "something went wrong" rather than crash destructuring it.
 */
export function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.status === 'number' && typeof v.code === 'string'
}

/** Normalises anything a failed call can produce into a `DecaApiError`. */
export function toApiError(error: unknown, response?: Response): DecaApiError {
  if (error instanceof DecaApiError) return error
  if (isProblem(error)) return new DecaApiError(error, response)

  const status = response?.status ?? 0
  return new DecaApiError(
    {
      type: 'about:blank',
      title: response?.statusText || 'Network or unexpected error',
      status,
      code: status ? `HTTP_${status}` : 'NETWORK_ERROR',
      detail: error instanceof Error ? error.message : undefined,
    },
    response,
  )
}

// ---------------------------------------------------------------------------
// Per-party validation helpers — these back the "blocked generation" screen
// (design spec §6, screen 5).
// ---------------------------------------------------------------------------

/** Only the failures that block generation. `WARNING`s (NIF/plate checksums) do not. */
export function blockingErrors(p: ValidationProblem): FieldError[] {
  return (p.partyErrors ?? []).flatMap((pe) => pe.fields.filter((f) => f.severity !== 'WARNING'))
}

export function warnings(p: ValidationProblem): FieldError[] {
  return (p.partyErrors ?? []).flatMap((pe) => pe.fields.filter((f) => f.severity === 'WARNING'))
}

/** Generation is blocked only by `ERROR`s — a document may generate carrying warnings. */
export function blocksGeneration(p: ValidationProblem): boolean {
  return blockingErrors(p).length > 0
}

/**
 * Errors for one party, for the "Faltan datos del Cargador: …" summary.
 * Field order follows the server, which follows the template's column order.
 */
export function errorsForParty(p: ValidationProblem, party: LiableParty): FieldError[] {
  return (p.partyErrors ?? []).filter((pe) => pe.party === party).flatMap((pe) => pe.fields)
}

/** Every party that has at least one blocking failure — drives which sections go red. */
export function partiesAtFault(p: ValidationProblem): LiableParty[] {
  return (p.partyErrors ?? [])
    .filter((pe) => pe.fields.some((f) => f.severity !== 'WARNING'))
    .map((pe) => pe.party)
}

/**
 * Field-keyed lookup for wiring server errors back onto react-hook-form inputs.
 * Grouped DeCAs repeat column names across consignments, so those are keyed
 * `consignments.<seq>.<field>` to stay addressable.
 */
export function errorsByField(p: ValidationProblem): Record<string, FieldError> {
  const out: Record<string, FieldError> = {}
  for (const pe of p.partyErrors ?? []) {
    for (const f of pe.fields) {
      const key =
        f.consignmentSeq == null ? f.field : `consignments.${f.consignmentSeq}.${f.field}`
      out[key] = f
    }
  }
  return out
}

/** The €401-per-party notice, when the server attached one. */
export function penaltyNotice(p: ValidationProblem): { amountEur?: number; perParty?: boolean } | undefined {
  return p.penaltyNotice
}
